"use client";

import type { ReactNode } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { QuantityField } from "@/components/tools/physics-mechanics/quantity-field";
import { Working } from "@/components/tools/physics-mechanics/working";
import { t } from "@/lib/i18n/interpolate";
import {
  formatPlain,
  formatSig,
  type NumberFormatter,
  withUnit,
} from "@/lib/tool-kit/physics-mechanics/format";
import { parseQuantity } from "@/lib/tool-kit/physics-mechanics/quantity";
import {
  type Evaluation,
  type RelationDef,
  type RelationState,
  rightHandSide,
  substituteSymbols,
  workingSymbol,
  workingValue,
} from "@/lib/tool-kit/physics-mechanics/relation";
import {
  fromBase,
  unitOptions,
  unitSymbol,
} from "@/lib/tool-kit/physics-mechanics/units";

/**
 * The inputs and the answer for a relation solved for any one of its variables.
 *
 * Generic over the variable keys so each calculator only declares its relation and its
 * strings. All visible text arrives through `strings` and `labels`.
 */
export interface RelationPanelStrings {
  solveFor: string;
  unitFor: string;
  calculated: string;
  workingHeading: string;
  stepEquation: string;
  stepRearrange: string;
  stepConvert: string;
  stepSubstitute: string;
  stepAnswerUnit: string;
  copy: string;
  copied: string;
  enterAll: string;
  unreadable: string;
  errors: Record<string, string>;
}

function plainNumber(fmt: NumberFormatter, value: number): string {
  return formatPlain(fmt, value);
}

export function RelationInputs<K extends string>({
  idPrefix,
  def,
  labels,
  state,
  onState,
  evaluation,
  strings,
  fmt,
  sig,
  hints = {},
}: {
  idPrefix: string;
  def: RelationDef<K>;
  labels: Record<K, string>;
  state: RelationState<K>;
  onState: (state: RelationState<K>) => void;
  evaluation: Evaluation<K>;
  strings: RelationPanelStrings;
  fmt: NumberFormatter;
  sig: number;
  hints?: Partial<Record<K, string>>;
}) {
  const patch = (key: K, change: Partial<{ value: string; unit: string }>) =>
    onState({
      ...state,
      values: { ...state.values, [key]: { ...state.values[key], ...change } },
    });
  return (
    <>
      <PillGroup
        label={strings.solveFor}
        name={`${idPrefix}-unknown`}
        onChange={(unknown) => onState({ ...state, unknown })}
        options={def.keys.map((key) => ({ value: key, label: labels[key] }))}
        value={state.unknown}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {def.keys.map((key) => {
          const entry = state.values[key];
          const isAnswer = key === state.unknown;
          const shown =
            isAnswer && evaluation.kind === "ok"
              ? formatSig(
                  fmt,
                  fromBase(evaluation.value, def.families[key], entry.unit),
                  sig
                )
              : null;
          return (
            <QuantityField
              calculated={isAnswer ? (shown ?? "") : null}
              hint={isAnswer ? strings.calculated : hints[key]}
              id={`${idPrefix}-${key}`}
              invalid={
                evaluation.kind === "unreadable" && evaluation.key === key
              }
              key={key}
              label={labels[key]}
              onUnit={(unit) => patch(key, { unit })}
              onValue={(value) => patch(key, { value })}
              unit={entry.unit}
              unitLabel={t(strings.unitFor, { name: labels[key] })}
              units={unitOptions(def.families[key])}
              value={entry.value}
            />
          );
        })}
      </div>
    </>
  );
}

function relationSteps<K extends string>(
  def: RelationDef<K>,
  state: RelationState<K>,
  all: Record<K, number>,
  strings: RelationPanelStrings,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const steps = [t(strings.stepEquation, { equation: def.equation })];
  if (state.unknown !== def.solvedFor) {
    steps.push(
      t(strings.stepRearrange, {
        symbol: def.symbols[state.unknown],
        form: def.rearranged[state.unknown],
      })
    );
  }
  const working = (key: K) =>
    withUnit(
      plainNumber(fmt, workingValue(def, key, all[key])),
      workingSymbol(def, key)
    );
  const converted = def.keys.filter((key) => {
    if (key === state.unknown) {
      return false;
    }
    const typed = state.values[key].unit;
    return workingSymbol(def, key) !== unitSymbol(def.families[key], typed);
  });
  if (converted.length > 0) {
    steps.push(
      t(strings.stepConvert, {
        list: converted
          .map((key) => {
            const typed = state.values[key];
            const typedNumber = parseQuantity(typed.value) ?? 0;
            return `${def.symbols[key]} = ${withUnit(
              plainNumber(fmt, typedNumber),
              unitSymbol(def.families[key], typed.unit)
            )} = ${working(key)}`;
          })
          .join("; "),
      })
    );
  }
  const unknown = state.unknown;
  const expression = rightHandSide(def.rearranged[unknown]);
  const substituted = substituteSymbols(def, expression, (key) => {
    const text = plainNumber(fmt, workingValue(def, key, all[key]));
    const symbol = workingSymbol(def, key);
    const shown = symbol === "°" ? `${text}°` : text;
    return text.startsWith("-") || text.startsWith("−") ? `(${shown})` : shown;
  });
  const answer = withUnit(
    formatSig(fmt, workingValue(def, unknown, all[unknown]), sig),
    workingSymbol(def, unknown)
  );
  steps.push(
    t(strings.stepSubstitute, {
      symbol: def.symbols[unknown],
      working: substituted,
      value: answer,
    })
  );
  const typedUnit = state.values[unknown].unit;
  if (
    workingSymbol(def, unknown) !== unitSymbol(def.families[unknown], typedUnit)
  ) {
    const shown = withUnit(
      formatSig(
        fmt,
        fromBase(all[unknown], def.families[unknown], typedUnit),
        sig
      ),
      unitSymbol(def.families[unknown], typedUnit)
    );
    steps.push(t(strings.stepAnswerUnit, { si: answer, shown }));
  }
  return steps;
}

export function RelationResults<K extends string>({
  def,
  labels,
  state,
  evaluation,
  strings,
  fmt,
  sig,
  extras,
}: {
  def: RelationDef<K>;
  labels: Record<K, string>;
  state: RelationState<K>;
  evaluation: Evaluation<K>;
  strings: RelationPanelStrings;
  fmt: NumberFormatter;
  sig: number;
  /** More cards and notes for a solved relation, given every value in SI. */
  extras?: (all: Record<K, number>, unknown: K) => ReactNode;
}) {
  if (evaluation.kind === "incomplete") {
    return <Notice>{strings.enterAll}</Notice>;
  }
  if (evaluation.kind === "unreadable") {
    return (
      <ErrorNotice>
        {t(strings.unreadable, { name: labels[evaluation.key] })}
      </ErrorNotice>
    );
  }
  if (evaluation.kind === "error") {
    return (
      <ErrorNotice>
        {strings.errors[evaluation.error] ??
          strings.errors["not-finite"] ??
          evaluation.error}
      </ErrorNotice>
    );
  }
  const unknown = state.unknown;
  const unit = state.values[unknown].unit;
  const answer = withUnit(
    formatSig(
      fmt,
      fromBase(evaluation.value, def.families[unknown], unit),
      sig
    ),
    unitSymbol(def.families[unknown], unit)
  );
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[unknown]} value={answer} />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={`${def.symbols[unknown]} = ${answer}`}
      />
      <Working
        heading={strings.workingHeading}
        steps={relationSteps(def, state, evaluation.all, strings, fmt, sig)}
      />
      {extras ? extras(evaluation.all, unknown) : null}
    </>
  );
}
